import './timeslotCalendar.scss';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useDynamicPrices, useTimeslots } from '~hooks';
import { ConsumerCategoriesPicker, SimpleCalendar, TimeslotPicker } from '~molecules';
import { ConsumerCategory, Timeslot } from '~types/appsync.types';
import { formatDateISO, formatTimeslotDateRange } from '~utils';

export interface TimeslotCalendarProps
  extends Omit<React.ComponentProps<typeof SimpleCalendar>, 'onDateSelect'> {
  /** Product slug for fetching timeslots */
  productSlug: string;
  /** Number of participants to validate against slot availability */
  participantsCount?: number;
  /** Callback when a date is selected */
  onDateSelect: (date: Date | undefined) => void;
  /** Callback when a timeslot is selected */
  onTimeslotSelect: (slot: Timeslot | undefined) => void;
  /** Currently selected timeslot */
  selectedTimeslot?: Timeslot;
  /** Available consumer categories for dynamic prices */
  consumerCategories?: ConsumerCategory[];
  /** Whether the product uses dynamic pricing */
  hasDynamicPricing?: boolean;
  /** Currency for formatted prices */
  currency?: string;
  /** When true, hide the calendar and show only the timeslot picker */
  hideCalendar?: boolean;
}

/**
 * TimeslotCalendar extends SimpleCalendar with timeslot selection logic.
 * It automatically fetches and displays available timeslots when a date is selected.
 */
const TimeslotCalendar = ({
  productSlug,
  participantsCount,
  onDateSelect,
  onTimeslotSelect,
  selectedDate,
  selectedTimeslot,
  consumerCategories = [],
  hasDynamicPricing = false,
  currency = 'EUR',
  hideCalendar = false,
  ...simpleCalendarProps
}: TimeslotCalendarProps) => {
  const now = useMemo(() => new Date(), []);
  const { dateFrom, dateTo } = selectedDate
    ? formatTimeslotDateRange(selectedDate, now)
    : { dateFrom: undefined, dateTo: undefined };
  const [timeslots, loadingSlots] = useTimeslots(productSlug, dateFrom, dateTo);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | undefined>(selectedTimeslot);
  const [executeDynamicPrices] = useDynamicPrices();
  const [categoryPrices, setCategoryPrices] = useState<
    Record<string, { date: string; values: Record<string, number> }>
  >({});
  const selectableConsumerCategories = useMemo(
    () => consumerCategories.filter((c) => !c.student),
    [consumerCategories],
  );
  const [selectedConsumerCategory, setSelectedConsumerCategory] = useState<string | null>(null);

  useEffect(() => {
    setSelectedSlot(selectedTimeslot);
  }, [selectedTimeslot]);

  useEffect(() => {
    if (!hasDynamicPricing || selectableConsumerCategories.length === 0) {
      setSelectedConsumerCategory(null);
      return;
    }
    setSelectedConsumerCategory((prev) => {
      if (prev && selectableConsumerCategories.some((c) => c.shortname === prev)) {
        return prev;
      }
      return selectableConsumerCategories[0]?.shortname ?? null;
    });
  }, [hasDynamicPricing, selectableConsumerCategories]);

  // Track previous date and product to detect actual changes
  const prevSelectedDateRef = useRef<Date | undefined>(selectedDate);
  const prevProductSlugRef = useRef<string>(productSlug);

  // Reset slot when date or product actually changes (not on first render)
  useEffect(() => {
    const prevDate = prevSelectedDateRef.current;
    const prevSlug = prevProductSlugRef.current;
    const dateChanged = prevDate?.getTime() !== selectedDate?.getTime();
    const productChanged = prevSlug !== productSlug;

    // Update refs
    prevSelectedDateRef.current = selectedDate;
    prevProductSlugRef.current = productSlug;

    // Only reset if date or product actually changed (not first render)
    if ((dateChanged && prevDate !== undefined) || productChanged) {
      setSelectedSlot(undefined);
      onTimeslotSelect(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, productSlug]);

  // Auto-select first available daily booking slot
  const firstAutoSelectableSlot = useMemo(() => {
    const first = timeslots[0];
    return first && first.dailyBooking && first.quotaAvailable > 0 ? first : undefined;
  }, [timeslots]);

  useEffect(() => {
    if (!selectedSlot && !loadingSlots && firstAutoSelectableSlot) {
      setSelectedSlot(firstAutoSelectableSlot);
      onTimeslotSelect(firstAutoSelectableSlot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstAutoSelectableSlot, selectedSlot, loadingSlots]);

  const handleDateSelect = (date: Date | undefined) => {
    onDateSelect(date);
  };

  const isoSelectedDate = useMemo(() => {
    if (!selectedDate) return null;
    return formatDateISO(selectedDate);
  }, [selectedDate]);

  const timeslotsExternalIds = useMemo(() => timeslots.map((slot) => slot.externalId), [timeslots]);

  const shouldFetchDynamicPrices =
    hasDynamicPricing &&
    !!selectedConsumerCategory &&
    !!isoSelectedDate &&
    timeslotsExternalIds.length > 0;

  const hasPricesForCurrentSelection = useMemo(() => {
    if (!selectedConsumerCategory || !isoSelectedDate) {
      return false;
    }
    const cached = categoryPrices[selectedConsumerCategory];
    return cached?.date === isoSelectedDate;
  }, [categoryPrices, isoSelectedDate, selectedConsumerCategory]);

  useEffect(() => {
    if (!shouldFetchDynamicPrices || hasPricesForCurrentSelection) {
      return;
    }
    let active = true;
    executeDynamicPrices({
      variables: {
        consumerCategoryShortname: selectedConsumerCategory,
        fromDate: isoSelectedDate,
        nbDays: 1,
        productSlug,
        timeslotsExternalIds,
      },
    })
      .then((response) => {
        if (!active) return;
        const match = response?.data?.dynamicPrices?.find(
          (item) => item?.consumerCategoryShortname === selectedConsumerCategory,
        );
        const values = match?.datesCollection?.reduce<Record<string, number>>((acc, entry) => {
          if (entry?.timeslotExternalId && typeof entry.price === 'number') {
            acc[entry.timeslotExternalId] = entry.price;
          }
          return acc;
        }, {});
        setCategoryPrices((prev) => ({
          ...prev,
          [selectedConsumerCategory]: {
            date: isoSelectedDate,
            values: values ?? {},
          },
        }));
      })
      .catch(() => {
        if (!active) return;
        setCategoryPrices((prev) => ({
          ...prev,
          [selectedConsumerCategory]: {
            date: isoSelectedDate,
            values: {},
          },
        }));
      });
    return () => {
      active = false;
    };
  }, [
    timeslotsExternalIds,
    executeDynamicPrices,
    hasDynamicPricing,
    hasPricesForCurrentSelection,
    isoSelectedDate,
    productSlug,
    selectedConsumerCategory,
    shouldFetchDynamicPrices,
  ]);

  const timeslotPrices = useMemo(() => {
    if (!selectedConsumerCategory || !isoSelectedDate) {
      return undefined;
    }
    const entry = categoryPrices[selectedConsumerCategory];
    if (entry?.date !== isoSelectedDate) {
      return undefined;
    }
    return entry.values;
  }, [categoryPrices, isoSelectedDate, selectedConsumerCategory]);

  const isAwaitingDynamicPrices = shouldFetchDynamicPrices && !hasPricesForCurrentSelection;
  const isLoadingTimeslots = loadingSlots || isAwaitingDynamicPrices;

  const handleSelectTimeslot = (timeslot: Timeslot) => {
    setSelectedSlot(timeslot);
    onTimeslotSelect(timeslot);
  };

  return (
    <div className='TimeslotCalendar'>
      {!hideCalendar && (
        <SimpleCalendar
          {...simpleCalendarProps}
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
        />
      )}

      {(selectedDate || hideCalendar) && (
        <div className='TimeslotCalendar_timeslots'>
          {hasDynamicPricing && selectableConsumerCategories.length > 0 && (
            <ConsumerCategoriesPicker
              categories={selectableConsumerCategories}
              value={selectedConsumerCategory}
              onChange={setSelectedConsumerCategory}
              classNamePrefix='TimeslotCalendar'
              testIdPrefix='TimeslotCalendar'
            />
          )}
          <TimeslotPicker
            isLoading={isLoadingTimeslots}
            timeslots={timeslots}
            selected={selectedSlot}
            onTimeslotSelect={handleSelectTimeslot}
            participantsCount={participantsCount}
            currency={currency}
            timeslotPrices={timeslotPrices}
          />
        </div>
      )}
    </div>
  );
};

export default TimeslotCalendar;
